/**
 * Utility functions for handling extension fields in Data Vault Entities
 */

import { toMMDDYYYYFormat, parseDateOnlyString } from "../../utils/datetime";
import type { ExtensionFieldValueObjectType } from "../graphql";

/**
 * Union type for extension field values
 * Represents the possible values that can be stored in extension fields
 */
export type ExtensionFieldValue = string | number | null;

export const INTEGER_FIELD_MIN_VALUE = -2147483648;
export const INTEGER_FIELD_MAX_VALUE = 2147483647;

// Extension field type IDs mapping
export const EXTENSION_FIELD_TYPE_IDS = {
  INTEGER: 1,
  FLOAT: 2,
  TEXT: 3,
  ENUMERATION: 4,
  PERCENTILE: 7,
  CURRENCY_USD: 8,
  DATE: 9,
} as const;

/**
 * Get human-readable label for extension field type
 */
export const getFieldTypeLabel = (typeId: number): string => {
  switch (typeId) {
    case EXTENSION_FIELD_TYPE_IDS.INTEGER:
      return "Integer";
    case EXTENSION_FIELD_TYPE_IDS.FLOAT:
      return "Float";
    case EXTENSION_FIELD_TYPE_IDS.TEXT:
      return "Text";
    case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
      return "Enumeration";
    case EXTENSION_FIELD_TYPE_IDS.PERCENTILE:
      return "Percentile";
    case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
      return "Currency (USD)";
    case EXTENSION_FIELD_TYPE_IDS.DATE:
      return "Date";
    default:
      return "Unknown";
  }
};

/**
 * Format extension field value for display
 */
export const formatFieldValue = (
  value: ExtensionFieldValueObjectType,
  fieldType: number,
): string => {
  if (!value) return "";

  switch (fieldType) {
    case EXTENSION_FIELD_TYPE_IDS.INTEGER:
      return value.intValue?.toString() || "";
    case EXTENSION_FIELD_TYPE_IDS.FLOAT:
      return value.floatValue?.toString() || "";
    case EXTENSION_FIELD_TYPE_IDS.TEXT:
      return value.stringValue || "";
    case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
      return value.enumValue?.enumValueLabel || "";
    case EXTENSION_FIELD_TYPE_IDS.DATE: {
      if (!value.dateValue) return "";
      const dateString = value.dateValue;
      // Use timezone-safe parser to avoid conversion issues
      const parsedDate = parseDateOnlyString(dateString);
      if (parsedDate) {
        return toMMDDYYYYFormat(parsedDate);
      }
      // Fallback for non-standard date formats
      const date = new Date(dateString);
      return toMMDDYYYYFormat(date);
    }
    case EXTENSION_FIELD_TYPE_IDS.PERCENTILE:
      return typeof value.floatValue === "number" ? `${value.floatValue.toFixed(2)}%` : "";
    case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
      return typeof value.floatValue === "number"
        ? `$${value.floatValue.toLocaleString()}`
        : "";
    default:
      return value.stringValue || "";
  }
};

/**
 * Parse user input value based on field type
 */
export const parseFieldValue = (inputValue: string, fieldType: number): ExtensionFieldValue => {
  if (!inputValue.trim()) return null;

  switch (fieldType) {
    case EXTENSION_FIELD_TYPE_IDS.INTEGER: {
      const intValue = parseInt(inputValue, 10);
      return isNaN(intValue) ? null : intValue;
    }
    case EXTENSION_FIELD_TYPE_IDS.FLOAT:
    case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD: {
      const floatValue = parseFloat(inputValue);
      return isNaN(floatValue) ? null : floatValue;
    }
    case EXTENSION_FIELD_TYPE_IDS.PERCENTILE: {
      const percentValue = parseFloat(inputValue);
      if (isNaN(percentValue)) return null;
      return percentValue;
    }
    case EXTENSION_FIELD_TYPE_IDS.TEXT:
      return inputValue.trim();
    case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
      return inputValue;
    case EXTENSION_FIELD_TYPE_IDS.DATE:
      return inputValue;
    default:
      return inputValue;
  }
};

/**
 * Validate field value against type constraints
 */
export const validateFieldValue = (
  value: ExtensionFieldValue,
  fieldType: number,
): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  switch (fieldType) {
    case EXTENSION_FIELD_TYPE_IDS.INTEGER: {
      const numericValue = Number(value);

      if (!Number.isInteger(numericValue)) {
        return "Must be a valid integer";
      }

      if (numericValue < INTEGER_FIELD_MIN_VALUE || numericValue > INTEGER_FIELD_MAX_VALUE) {
        return `Must be between ${INTEGER_FIELD_MIN_VALUE} and ${INTEGER_FIELD_MAX_VALUE}`;
      }

      break;
    }
    case EXTENSION_FIELD_TYPE_IDS.FLOAT:
    case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
      if (isNaN(Number(value))) {
        return "Must be a valid number";
      }
      break;
    case EXTENSION_FIELD_TYPE_IDS.PERCENTILE: {
      const percentValue = Number(value);
      if (isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
        return "Must be a percentage between 0 and 100";
      }
      break;
    }
    case EXTENSION_FIELD_TYPE_IDS.TEXT:
      if (typeof value !== "string") {
        return "Must be text";
      }
      break;
  }

  return null;
};

/**
 * Check if value has changed from original
 */
export const isValueChanged = (
  currentValue: ExtensionFieldValue,
  originalValue: ExtensionFieldValueObjectType | null,
  fieldType: number,
): boolean => {
  if (!originalValue) return currentValue !== null && currentValue !== "";

  const originalFormatted = formatFieldValue(originalValue, fieldType);
  const currentFormatted = currentValue?.toString() || "";

  return originalFormatted !== currentFormatted;
};

/**
 * Serialize field value for GraphQL mutation
 *
 * Currently all field types use simple string conversion, but this function
 * exists to provide a centralized place for type-specific serialization logic
 * if needed in the future (similar to how formatFieldValue and parseFieldValue
 * handle different types differently).
 */
export const serializeFieldValue = (value: ExtensionFieldValue, _fieldType: number): string => {
  if (value === null || value === undefined) return "";

  // All current field types use simple string conversion
  // Future field types may require type-specific serialization logic
  return value.toString();
};

/**
 * Field type sorting order for consistent display grouping.
 * Text fields first, followed by numeric types, then date and enumeration.
 */
const FIELD_TYPE_SORT_ORDER: Record<number, number> = {
  [EXTENSION_FIELD_TYPE_IDS.TEXT]: 1,
  [EXTENSION_FIELD_TYPE_IDS.INTEGER]: 2,
  [EXTENSION_FIELD_TYPE_IDS.FLOAT]: 3,
  [EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD]: 4,
  [EXTENSION_FIELD_TYPE_IDS.PERCENTILE]: 5,
  [EXTENSION_FIELD_TYPE_IDS.DATE]: 6,
  [EXTENSION_FIELD_TYPE_IDS.ENUMERATION]: 7,
} as const;

/**
 * Sort extension field values by field type and then alphabetically by name.
 *
 * @param fieldValues Array of extension field values to sort
 * @returns Sorted array with fields grouped by type and alphabetically ordered within groups
 */
export const sortExtensionFieldValues = <
  T extends { field?: { id?: string; name?: string; extensionFieldTypeId: number } | null },
>(
  fieldValues: T[],
): T[] => {
  return fieldValues
    .filter((fieldValue) => fieldValue.field?.id)
    .sort((a, b) => {
      const fieldA = a.field;
      const fieldB = b.field;

      if (!fieldA || !fieldB) return 0;

      const typeOrderA = FIELD_TYPE_SORT_ORDER[fieldA.extensionFieldTypeId] ?? 999;
      const typeOrderB = FIELD_TYPE_SORT_ORDER[fieldB.extensionFieldTypeId] ?? 999;

      if (typeOrderA !== typeOrderB) {
        return typeOrderA - typeOrderB;
      }

      const nameA = fieldA.name || "";
      const nameB = fieldB.name || "";
      return nameA.localeCompare(nameB);
    });
};
